import * as React from 'react';

import type {
  ContentAction,
  IContentEditor,
  IContentRecord,
  IContentVersion,
  ISitePage
} from './contracts';
import { EDITORS, SITE_PAGES, VIEWER_ID } from './fixtures';

/**
 * The website content state that is still invented — one reducer, in memory, above the screens
 * (§7.11).
 *
 * **Posts and the team page are not in here any more.** Both are survey-api's — the posts under
 * `/api/v1/admin/website/posts`, the people under `…/team` — so they are read and written through
 * `api/posts-api.ts` and `api/team-api.ts` and held by the screen that opened them, like every
 * other real list in this app. What is left here is the three screens that still have no endpoint
 * — the site pages — and the roster their records are attributed to.
 *
 * **Nothing in it persists and nothing in it calls survey-api.** A reload puts the fixtures back.
 * That is the same rule the demo follows (`src/demo`), for the same reason: a mock that writes
 * somewhere durable starts being mistaken for the product.
 *
 * Three behaviours are modelled rather than faked, because they are the ones the requirements turn
 * on and the ones a form drawn over static data would quietly get wrong.
 *
 * **A save is checked against the revision it was opened at** (`saveWith`). WEB-11 AC1 asks that a
 * second writer be warned instead of silently overwriting the first, and a revision check is how
 * survey-api already does that on authoring drafts. So an editor holds the revision it opened,
 * and a save against a moved one is refused and says who moved it. `bumpAsColleague` is the one
 * affordance here with no counterpart in the product: it moves a record as another editor would,
 * so the refusal can be seen without a second browser. It is labelled as a mock control wherever
 * it is drawn.
 *
 * **A revert appends.** `revertTo` writes a new version whose note names the one it restored, so
 * the history keeps both the mistake and its correction (WEB-14 AC2, and `contracts.ts`).
 *
 * **Publishing is not modelled here at all any more.** It is survey-api's `content.publish`,
 * held by a role and reported by `/auth/me`, and the post editor reads it through `useCan`. The
 * roster below is only who a mock record is attributed to.
 *
 * The lists are flat arrays and the screens filter them. There are at most a few dozen rows of
 * anything here, so an index would be ceremony; if this domain ever reads a real API, the
 * filtering moves to the server and these selectors go with it.
 */

interface IContentState {
  pages: ISitePage[];
  editors: IContentEditor[];
}

const INITIAL: IContentState = {
  pages: SITE_PAGES,
  editors: EDITORS
};

/** A write that carries the revision it was composed against. */
export interface IRevisioned {
  id: string;
  baseRevision: number;
}

export type ContentEvent =
  | { kind: 'page-save'; write: IRevisioned & Partial<ISitePage> }
  | { kind: 'status'; of: RecordKind; id: string; to: ContentAction; by: string }
  | { kind: 'revert'; of: RecordKind; id: string; versionNo: number; by: string }
  | { kind: 'hold'; of: RecordKind; id: string; by: Nullable<string> }
  | { kind: 'colleague'; of: RecordKind; id: string; by: string };

export type RecordKind = 'page';

const nowIso = () => new Date().toISOString();

/** The next history entry, and the revision that goes with it. */
function advance(
  record: IContentRecord,
  action: ContentAction,
  by: string,
  note?: string
): Pick<IContentRecord, 'revision' | 'updatedAt' | 'updatedBy' | 'history'> {
  const versionNo = (record.history.at(-1)?.versionNo ?? 0) + 1;
  const entry: IContentVersion = { versionNo, at: nowIso(), by, action, ...(note ? { note } : {}) };
  return {
    revision: record.revision + 1,
    updatedAt: entry.at,
    updatedBy: by,
    history: [...record.history, entry]
  };
}

/** `status` carries an action; this is the state it leaves the record in. */
function statusAfter(action: ContentAction): IContentRecord['status'] | null {
  if (action === 'published') return 'published';
  if (action === 'unpublished') return 'archived';
  return null;
}

/**
 * Apply `change` to the record `id` in `list`, or leave the list alone when the write is stale.
 *
 * Staleness is the whole point — a refused write must change nothing at all, including the
 * history, so the caller's own conflict message is the only thing the viewer sees.
 */
function saveWith<T extends IContentRecord>(
  list: T[],
  write: IRevisioned & Partial<T>,
  by: string
): T[] {
  return list.map((record) => {
    if (record.id !== write.id) return record;
    if (record.revision !== write.baseRevision) return record;
    const { id: _id, baseRevision: _base, ...patch } = write;
    return { ...record, ...patch, ...advance(record, 'saved', by) } as T;
  });
}

function withStatus<T extends IContentRecord>(
  list: T[],
  id: string,
  action: ContentAction,
  by: string
): T[] {
  return list.map((record) => {
    if (record.id !== id) return record;
    const status = statusAfter(action);
    return {
      ...record,
      ...(status ? { status } : {}),
      ...(action === 'published' ? { submittedAt: null } : {}),
      ...advance(record, action, by)
    };
  });
}

function withRevert<T extends IContentRecord>(
  list: T[],
  id: string,
  versionNo: number,
  by: string
): T[] {
  return list.map((record) =>
    record.id === id
      ? {
          ...record,
          ...advance(record, 'reverted', by, `Restored version ${versionNo}`)
        }
      : record
  );
}

/** A colleague's save, so the revision moves under an open editor (WEB-11 AC1). */
function withColleague<T extends IContentRecord>(list: T[], id: string, by: string): T[] {
  return list.map((record) =>
    record.id === id ? { ...record, ...advance(record, 'saved', by) } : record
  );
}

function withHold<T extends IContentRecord>(list: T[], id: string, by: Nullable<string>): T[] {
  return list.map((record) => (record.id === id ? { ...record, heldBy: by } : record));
}

function reducer(state: IContentState, event: ContentEvent): IContentState {
  switch (event.kind) {
    case 'page-save':
      return { ...state, pages: saveWith(state.pages, event.write, viewerName(state)) };

    case 'status':
      return applyToKind(state, event.of, (list) => withStatus(list, event.id, event.to, event.by));

    case 'revert':
      return applyToKind(state, event.of, (list) =>
        withRevert(list, event.id, event.versionNo, event.by)
      );

    case 'hold':
      return applyToKind(state, event.of, (list) => withHold(list, event.id, event.by));

    case 'colleague':
      return applyToKind(state, event.of, (list) => withColleague(list, event.id, event.by));

  }
}

/** One record kind is left with no endpoint, and the kind is kept in the event because the
 *  reducer's shape is what the next one will slot into. */
function applyToKind(
  state: IContentState,
  _kind: RecordKind,
  apply: <T extends IContentRecord>(list: T[]) => T[]
): IContentState {
  return { ...state, pages: apply(state.pages) };
}

function viewerName(state: IContentState): string {
  return state.editors.find((editor) => editor.accountId === VIEWER_ID)?.name ?? 'You';
}

interface IContentContext {
  state: IContentState;
  send: React.Dispatch<ContentEvent>;
  /** The account these screens act as, with its capabilities as the access screen left them. */
  viewer: IContentEditor;
}

const Context = React.createContext<Nullable<IContentContext>>(null);

export function ContentProvider({ children }: { children: React.ReactNode }) {
  const [state, send] = React.useReducer(reducer, INITIAL);
  const viewer = state.editors.find((editor) => editor.accountId === VIEWER_ID) ?? state.editors[0];
  const value = React.useMemo(() => ({ state, send, viewer }), [state, send, viewer]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useContent(): IContentContext {
  const context = React.useContext(Context);
  if (!context) throw new Error('useContent outside ContentProvider');
  return context;
}

/* ── derived reads ─────────────────────────────────────────────────────────────────────── */

/**
 * An empty field in `seo` is a page that will be presented by whatever the template defaults to.
 *
 * The sharing image counts as a gap only where the screen offers a control for one. A screen with
 * nowhere to save a picture would otherwise be reporting the absence of a feature as somebody's
 * unfinished work.
 */
export function seoGaps(seo: { title: string; description: string; socialImageId?: Nullable<string> }): string[] {
  const missing: string[] = [];
  if (!seo.title.trim()) missing.push('page title');
  if (!seo.description.trim()) missing.push('description');
  if (seo.socialImageId === null) missing.push('sharing image');
  return missing;
}
